import { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ButtonLink, SidebarBrandLogo, SidebarLayout, type SidebarNavGroup, type SidebarNavItem } from '@slai/ui';
import {
    BarChart3,
    Bot,
    GitBranch,
    KeyRound,
    LayoutDashboard,
    MoveLeft,
    Package,
    Phone,
    Settings,
    ShieldCheck,
    Users,
    type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole, hasAnyRole, type UserRoleType } from 'types/userRoles';
import { ONBOARDING_BASE_URL } from 'utils/tenantStatusGate';

type NavItemConfig = {
    id: string;
    label: string;
    route: string;
    icon: LucideIcon;
    roles: UserRoleType[];
    /** `route` is an absolute URL to another app — needs a full page load, not a router link. */
    external?: boolean;
};

const navItems: NavItemConfig[] = [
    {
        id: 'dashboard',
        label: 'Dashboard',
        route: '/dashboard',
        icon: LayoutDashboard,
        roles: [UserRole.AGENT, UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN],
    },
    {
        id: 'access-manager',
        label: 'Access Manager',
        route: '/access-manager',
        icon: KeyRound,
        // Platform roles: an agent here manages nobody on the Platform, even
        // when they run another product's people (they do that there).
        roles: [UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN],
    },
    {
        id: 'agentic-workflow',
        label: 'Agentic Workflow',
        route: '/agentic-workflow',
        icon: GitBranch,
        roles: [UserRole.AGENT, UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN],
    },
    {
        id: 'contact-manager',
        label: 'Contact Manager',
        route: '/contact-manager',
        icon: Users,
        roles: [UserRole.AGENT, UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN],
    },
    {
        id: 'agent-command-center',
        label: 'Agent Command Center',
        route: '/agent-command-center',
        icon: Bot,
        roles: [UserRole.AGENT],
    },
];

const managerItems: NavItemConfig[] = [
    {
        id: 'manager-command-center',
        label: 'Manager Command Center',
        route: '/manager-command-center',
        icon: BarChart3,
        roles: [UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN],
    },
];

const superAdminItems: NavItemConfig[] = [
    {
        id: 'super-admin-data-entry',
        label: 'Super Admin Data Entry',
        route: '/super-admin-data-entry',
        icon: Settings,
        roles: [UserRole.SUPER_ADMIN],
    },
    {
        id: 'phone-number-manager',
        label: 'Phone Number Manager',
        route: '/phone-number-manager',
        icon: Phone,
        roles: [UserRole.SUPER_ADMIN],
    },
    {
        id: 'tenant-config',
        label: 'Tenant Config (read-only)',
        route: '/tenant-config',
        icon: ShieldCheck,
        roles: [UserRole.SUPER_ADMIN],
    },
    {
        id: 'company-products',
        label: 'Company Products',
        route: '/company-products',
        icon: Package,
        roles: [UserRole.SUPER_ADMIN],
    },
];

// Onboarding rejects AGENT-only sessions (no effective role), so it is not offered to them.
const onboardingItems: NavItemConfig[] = [
    {
        id: 'back-to-onboarding',
        label: 'Back to Onboarding',
        route: ONBOARDING_BASE_URL,
        icon: MoveLeft,
        roles: [UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN],
        external: true,
    },
];

type NavGroupConfig = {
    id: string;
    label?: string;
    items: NavItemConfig[];
};

const groupsItems: NavGroupConfig[] = [
    {
        id: 'main',
        label: 'SLAI operations',
        items: navItems,
    },
    {
        id: 'manager',
        label: 'Manager operations',
        items: managerItems,
    },
    {
        id: 'super-admin',
        label: 'Super Admin operations',
        items: superAdminItems,
    },
    {
        id: 'onboarding',
        items: onboardingItems,
    },
];

const isRouteActive = (pathname: string, route: string): boolean =>
    pathname === route || pathname.startsWith(`${route}/`);

const AppSidebar = () => {
    const { userRoles } = useAuth();
    const location = useLocation();
    const roles = Array.isArray(userRoles) ? userRoles : [];
    const shouldShowAllItems = roles.length === 0;

    const groups = useMemo((): SidebarNavGroup[] => {
        return groupsItems.map((group) => {
            const items: SidebarNavItem[] = group.items.map((item) => {
                const hasAccess = shouldShowAllItems || hasAnyRole(roles, item.roles);

                return {
                    id: item.id,
                    label: item.label,
                    icon: item.icon,
                    hidden: !hasAccess,
                    isActive: !item.external && isRouteActive(location.pathname, item.route),
                    linkComponent: item.external ? (
                        <ButtonLink
                            className='w-full mt-md'
                            href={item.route}
                            data-label={item.label}
                            variant='primary'
                            size='sm'
                        />
                    ) : (
                        <NavLink to={item.route} data-label={item.label} end={item.route === '/dashboard'} />
                    ),
                };
            });

            return { ...group, items };
        });
    }, [location.pathname, roles, shouldShowAllItems]);

    return (
        <SidebarLayout
            brand={{
                title: 'SLAI',
                logo: <SidebarBrandLogo />,
            }}
            groups={groups}
        />
    );
};

export default AppSidebar;
